/**
 * Client-side intake rules for the dossier upload.
 *
 * These are a courtesy, not a control: the server enforces the real caps and
 * its answer is always what the reader ends up seeing. What they buy is that a
 * 90 MB scan or a .docx does not travel over the wire before someone learns it
 * was never going to be accepted — the brief asks for "size/page caps with
 * clear messages", and a clear message that arrives after a two-minute upload
 * is not a clear message.
 *
 * Keep the numbers in sync with the intake endpoint — they mirror
 * `Settings.max_upload_files / max_file_mb / max_upload_mb / max_upload_pages`
 * in app/config.py (defaults 40 / 60 MB / 100 MB / 200 pages). They live here,
 * once.
 */
export const MAX_FILES = 40
export const MAX_FILE_BYTES = 60 * 1024 * 1024
export const MAX_TOTAL_BYTES = 100 * 1024 * 1024
/**
 * Pages are counted server-side — reading a page count in the browser would
 * mean parsing the PDF here, i.e. a PDF library in the bundle for a check the
 * server has to repeat anyway. The number is shown so the cap is not a surprise.
 */
export const MAX_PAGES = 200

/** How much of the file is sniffed for the %PDF- signature (per PDF spec). */
const SNIFF_BYTES = 1024
const PDF_MAGIC = '%PDF-'

export type RejectReason =
  | 'notPdf'
  | 'tooLarge'
  | 'totalTooLarge'
  | 'tooMany'
  | 'duplicate'
  | 'empty'

export interface Candidate {
  file: File
  /** Stable identity for React keys and duplicate detection. */
  key: string
  reason: RejectReason | null
}

export function fileKey(file: File): string {
  return `${file.name}::${file.size}`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${(bytes / 1024).toFixed(0)} KB`
}

/**
 * Content sniffing, not extension trust: `plan.pdf` that is really a JPEG is
 * exactly the mislabeled-extension case the brief lists.
 */
export async function looksLikePdf(file: File): Promise<boolean> {
  try {
    const head = await file.slice(0, SNIFF_BYTES).arrayBuffer()
    const text = new TextDecoder('latin1').decode(head)
    return text.includes(PDF_MAGIC)
  } catch {
    // Unreadable file: let the server be the one to refuse it, with a reason.
    return true
  }
}

/**
 * Fold newly dropped files into the existing selection. Pure apart from the
 * sniff, and it never throws — every rejection comes back attached to its file
 * so the list can say which file was refused and why.
 */
export async function admit(
  existing: readonly Candidate[],
  incoming: readonly File[],
): Promise<Candidate[]> {
  const next: Candidate[] = [...existing]
  const accepted = () => next.filter((candidate) => candidate.reason === null)

  for (const file of incoming) {
    const key = fileKey(file)
    if (next.some((candidate) => candidate.key === key)) {
      next.push({ file, key: `${key}::${next.length}`, reason: 'duplicate' })
      continue
    }
    if (file.size === 0) {
      next.push({ file, key, reason: 'empty' })
      continue
    }
    if (file.size > MAX_FILE_BYTES) {
      next.push({ file, key, reason: 'tooLarge' })
      continue
    }
    if (!(await looksLikePdf(file))) {
      next.push({ file, key, reason: 'notPdf' })
      continue
    }
    if (accepted().length >= MAX_FILES) {
      next.push({ file, key, reason: 'tooMany' })
      continue
    }
    const total = accepted().reduce((sum, candidate) => sum + candidate.file.size, 0)
    if (total + file.size > MAX_TOTAL_BYTES) {
      next.push({ file, key, reason: 'totalTooLarge' })
      continue
    }
    next.push({ file, key, reason: null })
  }

  return next
}
